<template>
  <div class="language-selector">
    <!-- 所有可用语言网格 -->
    <div class="language-grid-section">
      <div class="language-grid">
        <div
          v-for="lang in safeAllLanguages"
          :key="lang.languageCode"
          class="lang-card"
          :class="{ selected: isSelected(lang.languageCode) }"
          @click="toggleLanguage(lang)"
        >
          <span class="lang-badge">{{ formatLangDisplayLabel(lang) }}</span>
          <span class="lang-name">{{ lang.languageName }}</span>
        </div>
      </div>
    </div>

    <!-- 源语言区（浅紫背景，可拖拽与目的语言互换） -->
    <div class="selected-panel source-panel">
      <h4 class="panel-title">{{ t('speechTranslation.sourceLanguageLabel') }}</h4>
      <div class="panel-content">
        <VueDraggable
          v-model="localSource"
          :animation="200"
          group="lang-exchange"
          handle=".chip-drag"
          class="chip-list"
          :sort="false"
          @add="onSourceAdd"
          @end="onSourceDragEnd"
        >
          <div
            v-for="item in localSource"
            :key="item.languageCode"
            class="lang-chip"
          >
            <span class="chip-drag" :title="t('speechTranslation.dragToReplace')">
              <HolderOutlined />
            </span>
            <span class="chip-text">{{ formatLangDisplayLabel(item) }} {{ item.languageName }}</span>
            <span class="chip-remove" @click.stop="removeSource">
              <CloseOutlined />
            </span>
          </div>
        </VueDraggable>
        <span v-if="localSource.length === 0" class="placeholder">{{ t('speechTranslation.clickToSelectSource') }}</span>
      </div>
    </div>

    <!-- 目的语言区（浅紫背景，可拖拽排序、与源语言互换） -->
    <div class="selected-panel target-panel">
      <h4 class="panel-title">{{ t('speechTranslation.targetLanguageLabel') }}</h4>
      <div class="panel-content">
        <VueDraggable
          v-model="localTargets"
          :animation="200"
          group="lang-exchange"
          handle=".chip-drag"
          class="chip-list"
          @end="onTargetDragEnd"
        >
          <div
            v-for="item in localTargets"
            :key="item.languageCode"
            class="lang-chip"
          >
            <span class="chip-drag" :title="t('speechTranslation.dragToSort')">
              <HolderOutlined />
            </span>
            <span class="chip-text">{{ formatLangDisplayLabel(item) }} {{ item.languageName }}</span>
            <span class="chip-remove" @click.stop="removeTargetByCode(item.languageCode)">
              <CloseOutlined />
            </span>
          </div>
        </VueDraggable>
        <span v-if="localTargets.length === 0" class="placeholder">
          {{ t('speechTranslation.clickToSelectTarget') }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { message } from 'ant-design-vue'
import { HolderOutlined, CloseOutlined } from '@ant-design/icons-vue'
import { VueDraggable } from 'vue-draggable-plus'
import type { LangItem } from '../api/language'
import { formatLangDisplayLabel } from '../utils/lang-display-label'
import { isLangSttSupported } from '../utils/lang-stt-supported'

const { t } = useI18n()

const props = defineProps<{
  allLanguages: LangItem[]
  sourceLanguage: LangItem | null
  targetLanguages: LangItem[]
}>()

/** 过滤掉无效项，防止 API 返回或扁平化导致 undefined 引发 Cannot read 'languageCode' */
const safeAllLanguages = computed(() =>
  (props.allLanguages || []).filter((l): l is LangItem => l != null && typeof l.languageCode === 'string')
)

const emit = defineEmits<{
  (e: 'update:sourceLanguage', v: LangItem | null): void
  (e: 'update:targetLanguages', v: LangItem[]): void
}>()

function isSelected(code: string) {
  if (props.sourceLanguage?.languageCode === code) return true
  return props.targetLanguages.some((t) => t.languageCode === code)
}

function rejectUnsupportedSource() {
  message.warning(t('speechTranslation.sourceLanguageNotSupported'))
}

function toggleLanguage(lang: LangItem) {
  const code = lang.languageCode
  if (props.sourceLanguage?.languageCode === code) {
    const idx = props.targetLanguages.findIndex((t) => t.languageCode === code)
    if (idx >= 0) {
      emit('update:targetLanguages', props.targetLanguages.filter((_, i) => i !== idx))
    }
    emit('update:sourceLanguage', null)
    return
  }
  const targetIdx = props.targetLanguages.findIndex((t) => t.languageCode === code)
  if (targetIdx >= 0) {
    emit('update:targetLanguages', props.targetLanguages.filter((_, i) => i !== targetIdx))
    return
  }
  if (!props.sourceLanguage) {
    if (!isLangSttSupported(lang)) {
      rejectUnsupportedSource()
      return
    }
    emit('update:sourceLanguage', lang)
  } else {
    emit('update:targetLanguages', [...props.targetLanguages, lang])
  }
}

function removeSource() {
  emit('update:sourceLanguage', null)
}

function removeTargetByCode(code: string) {
  emit(
    'update:targetLanguages',
    props.targetLanguages.filter((t) => t.languageCode !== code)
  )
}

function emitSelection() {
  const src = localSource.value[0] ?? null
  emit('update:sourceLanguage', src)
  emit('update:targetLanguages', [...localTargets.value])
}

/**
 * 从目标语言拖入源语言时触发（Sortable @add 事件）。
 * 使用 nextTick 确保在 v-model 同步后再读取状态，避免竞态。
 * 逻辑：新拖入项 → 新源语言；原源语言 → 移到目标列表末尾
 */
function onSourceAdd(evt: { newIndex?: number; oldIndex?: number }) {
  nextTick(() => {
    const srcList = localSource.value
    if (srcList.length <= 1) {
      const only = srcList[0]
      if (only && !isLangSttSupported(only)) {
        // 从目标拖入空源区：拒绝并回退
        localSource.value = props.sourceLanguage ? [props.sourceLanguage] : []
        if (only.languageCode) {
          const already = localTargets.value.some((t) => t.languageCode === only.languageCode)
          if (!already) {
            localTargets.value = [...localTargets.value, only]
          }
        }
        rejectUnsupportedSource()
        emitSelection()
        return
      }
      emitSelection()
      return
    }
    const newIdx = Math.min(evt?.newIndex ?? srcList.length - 1, srcList.length - 1)
    const newSourceResolved = srcList[newIdx] ?? srcList[srcList.length - 1]
    const oldSourceResolved = srcList[1 - newIdx] ?? srcList[0]
    if (!isLangSttSupported(newSourceResolved)) {
      localSource.value = [oldSourceResolved]
      const nextTargets = [...localTargets.value, newSourceResolved]
      const seen = new Set<string>()
      localTargets.value = nextTargets.filter((t) => {
        const code = t.languageCode || ''
        if (seen.has(code)) return false
        seen.add(code)
        return true
      })
      rejectUnsupportedSource()
      emitSelection()
      return
    }
    if (newSourceResolved.languageCode === oldSourceResolved.languageCode) {
      localSource.value = [newSourceResolved]
      emitSelection()
      return
    }
    localSource.value = [newSourceResolved]
    // 按 languageCode 去重，防止多次快速替换后出现重复导致 Vue key 冲突（语种合并消失）
    const nextTargets = [...localTargets.value, oldSourceResolved]
    const seen = new Set<string>()
    localTargets.value = nextTargets.filter((t) => {
      const code = t.languageCode || ''
      if (seen.has(code)) return false
      seen.add(code)
      return true
    })
    emitSelection()
  })
}

function onSourceDragEnd() {
  // 仅在源区内拖拽时同步（源区只有1项，正常不应触发跨列表逻辑）
  emitSelection()
}

function onTargetDragEnd() {
  emitSelection()
}

const localSource = ref<LangItem[]>([])
const localTargets = ref<LangItem[]>([])

watch(
  () => props.sourceLanguage,
  (val) => {
    localSource.value = val ? [val] : []
  },
  { immediate: true }
)

watch(
  () => props.targetLanguages,
  (val) => {
    localTargets.value = val ? [...val] : []
  },
  { immediate: true }
)
</script>

<style scoped lang="less">
.language-selector {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-title,
.panel-title {
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
}

/* 不限制高度、不内嵌滚动，与会议翻译页整体滚动一致（小分辨率下无独立滚动条） */
.language-grid-section {
  overflow: visible;
  width: 100%;

  .language-grid {
    display: grid;
    width: 100%;
    /* 等宽列轨道 + 单元格内左对齐，保证多行卡片纵向左缘对齐 */
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    justify-content: start;
    justify-items: start;
    align-content: start;
    gap: 8px;
  }
}

/* 语言卡片：网格选择项（高度压缩以在小屏下无需滚动完整展示） */
.lang-card {
  display: inline-flex;
  align-items: center;
  min-width: 88px;
  height: 24px;
  padding: 0;
  border: 1px solid #91caff;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;

  .lang-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 24px;
    flex-shrink: 0;
    background: #1890ff;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    border-radius: 4px 0 0 4px;
  }

  .lang-name {
    flex: 1;
    padding: 0 6px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.85);
    white-space: nowrap;
  }

  &.selected {
    border-color: #1890ff;
    box-shadow: 0 0 0 1px #1890ff;
  }

  &:hover:not(.selected) {
    border-color: #40a9ff;
    background: #f0f8ff;
  }
}

.language-grid-section .lang-card:not(.selected) .lang-badge {
  background: #91caff;
}

/* 选中区域：浅紫背景、圆角 */
.selected-panel {
  padding: 8px 10px;
  background: #f5f0fa;
  border-radius: 10px;
  border: 1px solid #e8e0f0;

  .panel-title {
    margin-bottom: 6px;
  }

  .panel-content {
    min-height: 32px;
  }
}

/* 芯片列表：水平排列 */
.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

/* 已选语言芯片：浅灰底、白字、红x 置于右上角 */
.lang-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 20px 0 4px;
  background: #d9d9d9;
  border-radius: 6px;
  cursor: default;

  .chip-drag {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 24px;
    cursor: grab;
    color: rgba(0, 0, 0, 0.4);
    font-size: 12px;

    &:hover {
      color: rgba(0, 0, 0, 0.6);
    }

    &:active {
      cursor: grabbing;
    }
  }

  .chip-text {
    padding: 0 4px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.85);
    font-weight: 500;
    white-space: nowrap;
  }

  .chip-remove {
    position: absolute;
    top: 1px;
    right: 1px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
    background: #ff4d4f;
    color: #fff;
    font-size: 10px;
    border-radius: 50%;
    cursor: pointer;
    line-height: 1;

    &:hover {
      background: #ff7875;
    }
  }
}

.placeholder {
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}

@media (max-width: 1366px) {
  .language-selector {
    gap: 8px;
  }

  .lang-card {
    min-width: 76px;
    height: 22px;
  }

  .language-grid-section .language-grid {
    grid-template-columns: repeat(auto-fill, minmax(76px, 1fr));
  }

  .lang-card .lang-badge {
    width: 24px;
    height: 22px;
  }

  .lang-card .lang-name {
    padding: 0 5px;
    font-size: 10px;
  }

  .selected-panel {
    padding: 6px 8px;
  }

  .selected-panel .panel-title {
    margin-bottom: 4px;
  }

  .selected-panel .panel-content {
    min-height: 28px;
  }

  .chip-list {
    gap: 4px;
  }

  .lang-chip {
    height: 22px;
    padding: 0 18px 0 4px;
  }

  .lang-chip .chip-drag {
    height: 22px;
    width: 18px;
  }

  .lang-chip .chip-text {
    font-size: 10px;
  }
}
</style>
